/**
 * usePermissions hook - extracts user permissions from Auth0 token
 *
 * Validates: Requirements 1.2, 5.2, 5.3
 */

import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useCallback } from 'react';
import type { AppRole, UserPermissions, AuthContextValue } from './types';

/**
 * Namespace for custom claims in Auth0 tokens
 * Roles are stored at this path in the ID token
 */
export const ROLES_CLAIM_NAMESPACE = 'https://app.example.com/roles';

/**
 * Valid application roles
 */
export const VALID_ROLES: readonly AppRole[] = ['admin', 'policy_navigator', 'evidence_extractor'] as const;

/**
 * Extracts and validates roles from Auth0 user object
 * Returns only valid AppRole values, filtering out any invalid entries
 *
 * @param user - Auth0 user object with custom claims
 * @returns Array of valid AppRole values
 */
export function extractRolesFromUser(user: Record<string, unknown> | undefined): AppRole[] {
  if (!user) return [];

  const rolesFromClaim = user[ROLES_CLAIM_NAMESPACE];

  if (!Array.isArray(rolesFromClaim)) return [];

  // Filter to only valid roles, ensuring type safety
  return rolesFromClaim.filter(
    (role): role is AppRole => typeof role === 'string' && VALID_ROLES.includes(role as AppRole)
  );
}

/**
 * Hook that provides user permissions and role-checking utilities
 *
 * Extracts roles from Auth0 custom claims and provides helper functions
 * for checking user access to different parts of the application.
 *
 * @returns AuthContextValue with user permissions and helper functions
 */
export function usePermissions(): AuthContextValue {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const permissions = useMemo((): UserPermissions | null => {
    if (!user) return null;

    const roles = extractRolesFromUser(user as Record<string, unknown>);

    return {
      userId: user.sub ?? '',
      email: user.email ?? '',
      name: user.name ?? '',
      roles,
    };
  }, [user]);

  const hasRole = useCallback(
    (role: AppRole): boolean => {
      return permissions?.roles.includes(role) ?? false;
    },
    [permissions]
  );

  const hasAnyRole = useCallback(
    (roles: AppRole[]): boolean => {
      return roles.some((role) => hasRole(role));
    },
    [hasRole]
  );

  const login = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
        scope: 'openid profile email offline_access',
      },
    });
  }, [loginWithRedirect]);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    try {
      return await getAccessTokenSilently();
    } catch (error: unknown) {
      // Handle missing/expired refresh token by redirecting to login
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes('Missing Refresh Token') ||
        errorMessage.includes('invalid_grant') ||
        errorMessage.includes('Login required')
      ) {
        console.warn('Refresh token missing or expired, redirecting to login');
        loginWithRedirect({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
            scope: 'openid profile email offline_access',
          },
        });
        throw new Error('Authentication required - redirecting to login');
      }
      throw error;
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  return {
    isAuthenticated,
    isLoading,
    user: permissions,
    hasRole,
    hasAnyRole,
    login,
    logout: handleLogout,
    getAccessToken,
  };
}
